import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clamp } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  type EnhancedTabsConfigList,
  type TabConfigItem,
  type TabQueryOptions,
  type TabsControlInput,
} from './TabsMenu.types';
import { type ValueType } from '../core/Tabs.types';

import tabsMenuContext from './TabsMenu.context';

import { createTabQuery, getTabConfig, getTabsBounds, getVisibleTabs } from './TabsMenu.utils';

import useEvent from '@/shared/hooks/useEvent';
import useWindowEvent from '@/shared/hooks/useWindowEvent';

export type ConfigListReturn<T extends ValueType> = ReturnType<typeof useTabsConfigList<T>>;

type IndexGetterOptions<T extends ValueType> = {
  index: number | null | undefined;
  value: T | null | undefined;
};

type NavCallbacks = {
  onDigit?: (e: KeyboardEvent, digit: number) => void;
  onNext: () => void;
  onPrev: () => void;
};

type NavOptions = {
  enabled?: boolean;
};

const DIGIT_REGEX = /^(?:Digit|Numpad)(\d)$/;

const KEYS = {
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
} as const;

const useTabsConfigList = <T extends ValueType>(
  tabConfigs?: readonly TabConfigItem<T>[],
): EnhancedTabsConfigList<T> => {
  const visibleTabs = useMemo(() => getVisibleTabs(tabConfigs), [tabConfigs]);

  const bounds = useMemo(() => getTabsBounds(visibleTabs), [visibleTabs]);

  const getList = useCallback(
    (options?: TabQueryOptions) =>
      options?.includeHidden === true ? tabConfigs ?? [] : visibleTabs,
    [tabConfigs, visibleTabs],
  );

  const getTabIndexAndConfigByQuery: EnhancedTabsConfigList<T>['get'] = useCallback(
    (query, options) => {
      const tabsList = getList(options);

      if ('index' in query) return getTabConfig(tabsList, query.index);

      return getTabConfig(tabsList, (config) => config.value === query.value);
    },
    [getList],
  );

  const indexToValue: EnhancedTabsConfigList<T>['indexToValue'] = useCallback(
    (index, options) => getTabIndexAndConfigByQuery({ index }, options)?.config.value ?? null,
    [getTabIndexAndConfigByQuery],
  );

  const valueToIndex: EnhancedTabsConfigList<T>['valueToIndex'] = useCallback(
    (value, options) => {
      const index = getTabIndexAndConfigByQuery({ value }, options)?.index ?? null;

      if (index !== null) return index;

      return typeof value === 'number' ? clamp(value, bounds.min, bounds.max) : bounds.min;
    },
    [bounds.max, bounds.min, getTabIndexAndConfigByQuery],
  );

  return useMemo(
    () => ({
      bounds,
      get: getTabIndexAndConfigByQuery,
      getList,
      indexToValue,
      input: tabConfigs,
      list: visibleTabs,
      valueToIndex,
    }),
    [
      bounds,
      getList,
      getTabIndexAndConfigByQuery,
      indexToValue,
      tabConfigs,
      visibleTabs,
      valueToIndex,
    ],
  );
};

const useTabsControl = <T extends ValueType>(input: TabsControlInput<T>) => {
  const [initialTabInput] = useState({
    index: input.defaultTabIndex,
    value: input.defaultTab,
  });

  const [firstTabConfig] = input.config.getList({ includeHidden: input.allowInitialHiddenTab });

  const isControlled = input.tab !== undefined || input.tabIndex !== undefined;

  const getTab = input.config.get;

  type TabRes = {
    config: TabConfigItem<T>;
    index: number;
  } | null;

  const mapCurrentTabOutput = useCallback(
    (res: TabRes) => ({
      config: res?.config ?? null,
      index: res?.index ?? null,
      value: res?.config.value ?? null,
    }),
    [],
  );

  const mapInitialTabOutput = useCallback(
    (res: TabRes) => ({
      config: res?.config ?? firstTabConfig,
      index: res?.index ?? input.config.bounds.min,
      value: res?.config?.value ?? firstTabConfig?.value,
    }),
    [firstTabConfig, input.config.bounds.min],
  );

  const currentTab = useMemo(() => {
    if (!isControlled) return undefined;

    const query = createTabQuery({ index: input.tabIndex, value: input.tab });
    const res = query ? getTab(query, { includeHidden: input.allowInitialHiddenTab }) : null;

    return mapCurrentTabOutput(res);
  }, [
    getTab,
    input.allowInitialHiddenTab,
    input.tab,
    input.tabIndex,
    isControlled,
    mapCurrentTabOutput,
  ]);

  const initialTab = useMemo(() => {
    const { index, value } = initialTabInput;

    const query = createTabQuery({ index, value });
    const res = query ? getTab(query, { includeHidden: input.allowInitialHiddenTab }) : null;

    return mapInitialTabOutput(res);
  }, [getTab, initialTabInput, input.allowInitialHiddenTab, mapInitialTabOutput]);

  const onChange = useEvent((value: T | null) => {
    const nextValue = value ?? firstTabConfig?.value;

    if (nextValue != null) input.onChange?.(nextValue);
  });

  const tabsProps = useMemo(
    () => ({
      defaultValue: initialTab.value,
      onChange,
      value: currentTab?.value,
    }),
    [initialTab.value, currentTab?.value, onChange],
  );

  return useMemo(
    () => ({
      currentTab,
      initialTab,
      isControlled,
      tabs: tabsProps,
    }),
    [currentTab, initialTab, isControlled, tabsProps],
  );
};

type TabIndexControlInput<T extends ValueType> = TabsControlInput<T>;

const useTabsIndexControl = <T extends ValueType>(input: TabIndexControlInput<T>) => {
  const control = useTabsControl(input);

  const prevIndexRef = useRef(control.initialTab.index);

  const [activeIndex, setActiveIndex] = useState(control.initialTab.index);

  const { valueToIndex } = input.config;

  const getIndex = useCallback(
    (options: IndexGetterOptions<T>) => {
      const { index, value } = options;

      if (index != null) return index;
      if (value == null) return control.initialTab.index;

      return valueToIndex(value);
    },
    [control.initialTab.index, valueToIndex],
  );

  const onChange = useEvent((index: number) => {
    const nextTab = input.config.get({ index })?.config;
    const nextValue = nextTab?.value;

    setActiveIndex(index);

    if (nextValue != null) input.onChange?.(nextValue);

    prevIndexRef.current = index;
  });

  useEffect(() => {
    if (!control.isControlled) return;

    const nextIndex = getIndex({
      index: control.currentTab?.index,
      value: control.currentTab?.value,
    });

    if (nextIndex === prevIndexRef.current) return;

    setActiveIndex(nextIndex);

    prevIndexRef.current = nextIndex;
  }, [
    control.currentTab?.index,
    control.currentTab?.value,
    control.isControlled,
    getIndex,
    onChange,
  ]);

  return useMemo(
    () => ({
      onChange,
      tab: activeIndex,
    }),
    [activeIndex, onChange],
  );
};

const useTabsContext = tabsMenuContext.useContextOptional;

const useTabsNavigationEvents = (callbacks: NavCallbacks, options?: NavOptions) => {
  const { onDigit, onNext, onPrev } = callbacks;

  const isValidEvent = (e: KeyboardEvent | WheelEvent) => !!e.altKey && options?.enabled !== false;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isValidEvent(e)) return;

    switch (e.key) {
      case KEYS.ARROW_LEFT:
        e.preventDefault();
        onPrev();
        break;
      case KEYS.ARROW_RIGHT:
        e.preventDefault();
        onNext();
        break;
      default:
        break;
    }

    const match = e.code.match(DIGIT_REGEX);

    if (!match) return;

    const digit = Number(match[1]);

    onDigit?.(e, digit);
  };

  const handleWheel = (e: WheelEvent) => {
    if (!isValidEvent(e)) return;

    e.preventDefault();

    if (e.deltaY > 0) onNext();
    else if (e.deltaY < 0) onPrev();
  };

  useWindowEvent('keydown', handleKeyDown);
  useWindowEvent('wheel', handleWheel, { passive: false });
};

const useTranslateLabel = () => {
  const { i18n, t } = useTranslation();

  const isKey = i18n.exists;

  return useCallback((key?: string) => (!!key && isKey(key) ? t(key) : null), [isKey, t]);
};

export {
  useTabsControl,
  useTabsConfigList,
  useTabsContext,
  useTabsIndexControl,
  useTabsNavigationEvents,
  useTranslateLabel,
};
